<template>
  <form
    class="w-full flex items-center"
    :class="classForm || brandClasses.form"
    role="search"
    @submit.prevent="submit"
  >
    <define-submit-button>
      <vf-loader v-if="loading" class="my-a ml-3 h-6 w-6" />
      <slot v-else :class="classSubmit || brandClasses.submit" :label="$t.searchSubmitLabel" name="submit">
        <base-button
          :aria-label="$t.searchSubmitLabel"
          :class="classSubmit || brandClasses.submit"
          type="submit"
        >
          <vf-icon :name="iconSubmit || iconNameSubmit" :size="iconSizeSubmit" />
        </base-button>
      </slot>
    </define-submit-button>

    <submit-button v-if="isSubmitPositionStart" />

    <vf-input
      ref="inputRef"
      v-model.trim="modelValue"
      class="grow o:bg-transparent"
      :class="classInput || brandClasses.input"
      :props-input="{ maxlength, 'aria-labelledby': id, 'enterkeyhint': 'search' }"
    >
      <template #label>
        <span
          class="pointer-events-none absolute-0 flex items-center"
          :class="[brandClasses.label, labelClasses]"
        >
          <span>{{ label || $t.searchLabel }}</span>
        </span>
      </template>
      <template #after>
        <slot :class-button="clearButtonClasses" :clear :has-value="!!modelValue" name="after">
          <base-button
            v-if="modelValue"
            :aria-label="$t.searchClearLabel"
            :class="[brandClasses.clear?.[clearButtonType], clearButtonClasses]"
            @click="clear"
          >
            <vf-icon
              v-if="clearButtonType === 'icon'"
              :name="iconNameClear"
              :size="iconSizeClear"
            />
            <span v-else>{{ $t.clear }}</span>
          </base-button>
        </slot>
      </template>
    </vf-input>

    <submit-button v-if="!isSubmitPositionStart" />
  </form>
</template>

<script lang="ts" setup>
import type { BaseInput as BaseInputType } from '#components'
import type { IconName } from '#types/icon'

const { submitPosition, clearButtonType = 'icon' } = defineProps<{
  /**
   * Defines visually hidden label for input
   */
  label?: string
  /**
   * Indicates loading state
   */
  loading?: boolean
  /**
   * Defines max length for input
   */
  maxlength?: number
  /**
   * Defines whether submit button appears before or after input
   */
  submitPosition?: 'start' | 'end'
  /**
   * Defines the clear button display type - icon or text 'Clear'
   */
  clearButtonType?: 'icon' | 'text'
  /**
   * Classes to be applied to form element
   */
  classForm?: string
  /**
   * Classes to be applied to input element
   */
  classInput?: string
  /**
   * Classes to be applied to submit button
   */
  classSubmit?: string
  /**
   * Overrides the configured submit icon name
   */
  iconSubmit?: IconName
}>()

const emit = defineEmits<{
  change: [value: string]
  clear: []
  focus: [value?: string]
  submit: [value: string]
}>()

const {
  brandClasses,
  defaultSubmitPosition = 'end',
  iconNameClear,
  iconNameSubmit,
  iconSizeClear,
  iconSizeSubmit
} = useAppConfig().components.vf.searchBar
const id = useId()

const modelValue = defineModel<string>()

const [DefineSubmitButton, SubmitButton] = createReusableTemplate()

const inputRef = ref<InstanceType<typeof BaseInputType>>()

const isSubmitPositionStart = computed(() => (submitPosition || defaultSubmitPosition) === 'start')

const clear = () => {
  emit('clear')
  modelValue.value = ''
  // Find the actual input element inside the component wrapper
  const el = inputRef.value?.$el
  const input = el instanceof HTMLInputElement ? el : el?.querySelector('input')
  input?.focus()
}

const submit = () => {
  if (modelValue.value) {
    emit('submit', modelValue.value)
  }
}

const labelClasses = computed(() => ({ 'op-0': !!modelValue.value }))
const clearButtonClasses = computed(() => ({ 'op-0': !modelValue.value }))

defineExpose({
  inputRef
})
</script>
